/**
 * clack renders every settled prompt and successful spinner stop with a green
 * `◇` (S_STEP_SUBMIT) and offers no settings hook to change it. Init wants
 * completed steps to read as successes — a green `✓`, matching the summary
 * checkmarks. This intercepts stdout writes and rewrites the exact styled
 * glyph sequence at submission; no cursor movement is ever involved, so the
 * check can never land on the wrong row.
 *
 * Only the colored sequence is matched: plain `◇` in content, uncolored
 * off-TTY output, and child processes with inherited stdio (which bypass
 * `process.stdout.write`) are all untouched.
 */

const GREEN_STEP_SUBMIT = '\x1b[32m◇\x1b[39m'
const GREEN_CHECK = '\x1b[32m✓\x1b[39m'

/** Replace clack's green submitted-step diamond with a green check. */
export function restyleStepSubmitSymbols(text: string): string {
  return text.split(GREEN_STEP_SUBMIT).join(GREEN_CHECK)
}

let installed = false

/**
 * Patch `process.stdout.write` to restyle submitted-step symbols for the rest
 * of the command run. Idempotent; no-op off-TTY (CI, pipes) where clack writes
 * uncolored fallbacks anyway. Returns a restore function (used by tests; a
 * command run just exits with the patch in place).
 */
export function installPromptCheckmarks(): () => void {
  if (installed || !process.stdout.isTTY || process.env.CI === 'true') return () => {}
  installed = true

  const unpatched = process.stdout.write
  const write = unpatched.bind(process.stdout) as (
    chunk: string | Uint8Array,
    encoding?: unknown,
    callback?: unknown
  ) => boolean

  process.stdout.write = ((chunk: string | Uint8Array, encoding?: unknown, callback?: unknown) => {
    if (typeof chunk !== 'string') return write(chunk, encoding, callback)
    return write(restyleStepSubmitSymbols(chunk), encoding, callback)
  }) as typeof process.stdout.write

  return () => {
    process.stdout.write = unpatched
    installed = false
  }
}
